import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useUsers } from './UserContext';
import { getHolidays, Holiday } from '../lib/api/holidays';
import { getSiteData } from '../lib/api/client';
import { format, parseISO, isValid } from 'date-fns';

interface HolidayContextType {
  holidays: Holiday[];
  loading: boolean;
  error: string | null;
  loadHolidays: (year: string | number) => Promise<Holiday[]>;
  isPublicHoliday: (date: Date | string) => boolean;
  clearCache: () => void;
}

const HolidayContext = createContext<HolidayContextType>({
  holidays: [],
  loading: false,
  error: null,
  loadHolidays: async () => [],
  isPublicHoliday: () => false,
  clearCache: () => {},
});

// Cache entry with TTL metadata
interface CacheEntry {
  data: Holiday[];
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
}

// TTL-based cache with LRU eviction
class HolidayCache {
  private cache = new Map<string, CacheEntry>();
  private readonly TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MAX_ENTRIES = 10; // Maximum 10 site-year entries

  get(key: string): Holiday[] | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if entry is expired (TTL exceeded)
    const now = Date.now();
    if (now - entry.timestamp > this.TTL_MS) {
      this.cache.delete(key);
      return null;
    }

    // Update access metadata for LRU
    entry.accessCount++;
    entry.lastAccessed = now;

    return entry.data;
  }

  set(key: string, data: Holiday[]): void {
    // Evict LRU entry if cache is full
    if (this.cache.size >= this.MAX_ENTRIES && !this.cache.has(key)) {
      this.evictLRU();
    }

    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      accessCount: 1,
      lastAccessed: now,
    });
  }

  private evictLRU(): void {
    let lruKey: string | null = null;
    let lruScore = Infinity;

    // Find least recently used entry (lowest lastAccessed)
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < lruScore) {
        lruScore = entry.lastAccessed;
        lruKey = key;
      }
    }

    if (lruKey) {
      this.cache.delete(lruKey);
    }
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

// Global cache instance
const holidayCache = new HolidayCache();

export function HolidayProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const { currentUser } = useUsers();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load holidays based on the current user's site location
  const loadHolidays = async (year: string | number) => {
    if (!token || !currentUser?.site) {
      return [];
    }

    const cacheKey = `${currentUser.site}-${year}`;
    
    // Return cached holidays if available and not expired
    const cachedData = holidayCache.get(cacheKey);
    if (cachedData) {
      setHolidays(cachedData);
      return cachedData;
    }

    setLoading(true);
    setError(null);

    try {
      // Get site data to determine location (BE, GB, etc.)
      const siteData = await getSiteData(currentUser.site);
      const location = siteData?.app?.location || 'BE'; // Default to BE if not specified
      
      // Fetch holidays for the specified year and location
      const holidayData = await getHolidays(year.toString(), location);
      
      // Cache the holidays with TTL
      holidayCache.set(cacheKey, holidayData);
      
      setHolidays(holidayData);
      return holidayData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load holidays';
      setError(errorMessage);
      console.error('Error loading holidays:', errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Clear cache (for admin operations like period changes)
  const clearCache = () => {
    holidayCache.clear();
    setHolidays([]);
  };

  // Load current year holidays on mount
  useEffect(() => {
    if (token && currentUser) {
      const currentYear = new Date().getFullYear();
      loadHolidays(currentYear);
    }
  }, [token, currentUser]);

  // Check if a date is a public holiday
  const isPublicHoliday = (date: Date | string): boolean => {
    try {
      // Normalize the date to a string format
      let dateStr: string;
      
      if (typeof date === 'string') {
        // Validate the string date
        const parsedDate = parseISO(date);
        if (!isValid(parsedDate)) {
          return false;
        }
        dateStr = format(parsedDate, 'yyyy-MM-dd');
      } else if (date instanceof Date) {
        // Validate the Date object
        if (!isValid(date)) {
          return false;
        }
        dateStr = format(date, 'yyyy-MM-dd');
      } else {
        return false;
      }

      // Check if the date is in the holidays array
      return holidays.some(holiday => holiday.date === dateStr);
    } catch (error) {
      console.warn('Error checking if date is a public holiday:', error);
      return false;
    }
  };

  return (
    <HolidayContext.Provider value={{ 
      holidays, 
      loading, 
      error, 
      loadHolidays,
      isPublicHoliday,
      clearCache
    }}>
      {children}
    </HolidayContext.Provider>
  );
}

export const useHolidays = () => useContext(HolidayContext);

// Export a standalone function that can be used without the context
export function isPublicHoliday(
  date: Date | string,
  holidays?: Holiday[] | Map<string, Holiday>
): boolean {
  if (!holidays || (Array.isArray(holidays) && holidays.length === 0) || 
      (holidays instanceof Map && holidays.size === 0)) {
    return false;
  }

  try {
    // Normalize the date to a string format
    let dateStr: string;
    
    if (typeof date === 'string') {
      // Validate the string date
      const parsedDate = parseISO(date);
      if (!isValid(parsedDate)) {
        return false;
      }
      dateStr = format(parsedDate, 'yyyy-MM-dd');
    } else if (date instanceof Date) {
      // Validate the Date object
      if (!isValid(date)) {
        return false;
      }
      dateStr = format(date, 'yyyy-MM-dd');
    } else {
      return false;
    }

    // Check if the date is in the holidays map or array
    if (holidays instanceof Map) {
      return holidays.has(dateStr);
    } else if (Array.isArray(holidays)) {
      return holidays.some(holiday => holiday.date === dateStr);
    }

    return false;
  } catch (error) {
    console.warn('Error checking if date is a public holiday:', error);
    return false;
  }
}